import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { HardDrive, Database, FileText, RefreshCw, Loader2, AlertTriangle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { getStorageStats } from '../lib/api';

const fmtBytes = (bytes) => {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let n = Number(bytes);
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n.toFixed(n < 10 ? 2 : 1)} ${units[i]}`;
};

// Fasi soglia per piani MongoDB tipici (Emergent Lancio: stima 5 GB storage utili)
// L'utente può cambiare la soglia se conosce il valore preciso
const DEFAULT_LIMIT_GB = 5;

export default function StorageStatsPanel() {
  const { toast } = useToast();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const res = await getStorageStats();
      setStats(res.data);
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }
  if (!stats) return null;

  const dbBytes = stats.db.storage_size_bytes + stats.db.index_size_bytes;
  const dbLimitBytes = DEFAULT_LIMIT_GB * 1024 * 1024 * 1024;
  const dbUsedPct = Math.min(100, (dbBytes / dbLimitBytes) * 100);
  const attBytes = stats.attachments.total_bytes;
  const attPct = dbBytes > 0 ? (attBytes / dbBytes) * 100 : 0;

  const warningLevel = dbUsedPct >= 90 ? 'critical' : dbUsedPct >= 70 ? 'warning' : 'ok';

  return (
    <div className="space-y-4" data-testid="storage-stats-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Utilizzo Storage</h2>
          <p className="text-sm text-gray-500">
            Monitoraggio dello spazio occupato dal database e dagli allegati caricati.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={refreshing} data-testid="storage-refresh-btn">
          {refreshing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Aggiorna
        </Button>
      </div>

      {/* DB overall */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Database className="w-4 h-4 text-blue-600" />
            Database MongoDB complessivo
          </CardTitle>
          <CardDescription className="text-xs">
            Stima basata su piano da {DEFAULT_LIMIT_GB} GB. Se il tuo piano è diverso puoi ignorare la % e leggere il valore assoluto.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-baseline justify-between">
            <div>
              <div className="text-2xl font-bold text-gray-900" data-testid="storage-db-used">
                {fmtBytes(dbBytes)}
              </div>
              <div className="text-xs text-gray-500">
                di ~{DEFAULT_LIMIT_GB} GB stimati &nbsp;·&nbsp; {dbUsedPct.toFixed(1)}%
              </div>
            </div>
            <Badge
              className={
                warningLevel === 'critical' ? 'bg-red-100 text-red-800' :
                warningLevel === 'warning' ? 'bg-amber-100 text-amber-800' :
                'bg-emerald-100 text-emerald-800'
              }
              data-testid="storage-status-badge"
            >
              {warningLevel === 'critical' ? 'CRITICO' : warningLevel === 'warning' ? 'ATTENZIONE' : 'OK'}
            </Badge>
          </div>
          <Progress value={dbUsedPct} className={warningLevel === 'critical' ? '[&>*]:bg-red-500' : warningLevel === 'warning' ? '[&>*]:bg-amber-500' : ''} />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2 text-xs">
            <div>
              <div className="text-gray-500">Dati (compressi)</div>
              <div className="font-medium">{fmtBytes(stats.db.storage_size_bytes)}</div>
            </div>
            <div>
              <div className="text-gray-500">Indici</div>
              <div className="font-medium">{fmtBytes(stats.db.index_size_bytes)}</div>
            </div>
            <div>
              <div className="text-gray-500">Documenti</div>
              <div className="font-medium">{stats.db.total_documents.toLocaleString('it-IT')}</div>
            </div>
            <div>
              <div className="text-gray-500">Collezioni</div>
              <div className="font-medium">{stats.db.collections}</div>
            </div>
          </div>
          {warningLevel !== 'ok' && (
            <div className={`text-xs flex items-start gap-2 p-2 rounded-md ${warningLevel === 'critical' ? 'bg-red-50 text-red-800' : 'bg-amber-50 text-amber-800'}`}>
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                {warningLevel === 'critical'
                  ? 'Spazio quasi esaurito. Considera l\'upgrade del piano oppure una migrazione degli allegati su storage esterno S3-compatibile.'
                  : 'Utilizzo elevato. Monitora il trend nelle prossime settimane.'}
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Attachments */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="w-4 h-4 text-orange-600" />
            Allegati Contratti (GridFS)
          </CardTitle>
          <CardDescription className="text-xs">
            File PDF caricati direttamente sui contratti. Sono lo spazio principale che cresce nel tempo.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <div className="text-xs text-gray-500">Spazio occupato</div>
              <div className="text-xl font-bold text-gray-900" data-testid="storage-attachments-bytes">
                {fmtBytes(attBytes)}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500">File caricati</div>
              <div className="text-xl font-bold text-gray-900" data-testid="storage-attachments-count">
                {stats.attachments.files_count.toLocaleString('it-IT')}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Media per file</div>
              <div className="text-xl font-bold text-gray-900">
                {fmtBytes(stats.attachments.avg_size_bytes)}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Quota su DB</div>
              <div className="text-xl font-bold text-gray-900">
                {attPct.toFixed(1)}%
              </div>
            </div>
          </div>
          <div className="text-xs text-gray-500 pt-1 border-t">
            <HardDrive className="w-3.5 h-3.5 inline mr-1 text-gray-400" />
            Storage GridFS: i file rimangono persistenti tra i deploy. Nessun rischio di perdita ai riavvii del pod.
          </div>
        </CardContent>
      </Card>

      <div className="text-xs text-gray-500 italic">
        Nota: il limite di {DEFAULT_LIMIT_GB} GB è una stima del piano Emergent &quot;Lancio&quot;. Per il valore preciso del tuo piano, contatta il supporto Emergent.
      </div>
    </div>
  );
}
